import * as t from "@rekajs/types";
import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import type { ComponentType } from "@repo/editor/types";
import {
  ImageIcon,
  TrashIcon,
  CameraIcon,
  LoaderCircle,
  XIcon,
  EditIcon,
} from "lucide-react";
import { observer } from "mobx-react-lite";
import { forwardRef, useEffect, useState } from "react";
import { useEditor } from "@repo/editor/useEditor";
import Image from "next/image";
import {
  MediaRepository,
  type Media as MediaType,
} from "@repo/fetch-repository/media";
import {
  CarouselContent,
  CarouselItem,
  Carousel,
} from "../components/ui/carousel";
import { QuoteIcon } from "../components/icons/quote";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";

export const commentsDefinition: ComponentSettingsProps = {
  comments: {
    type: "string",
    label: "لیست نظرات",
  },
  label: {
    type: "string",
    label: "عنوان",
  },
};

type SocialType = "instagram" | "linkedin" | "twitter" | "facebook";

interface Social {
  type: SocialType;
  url: string;
}

interface Comment {
  id: number;
  name: string;
  comment: string;
  image: string;
  social?: Social[];
  company?: string;
}

interface CommentsProps {
  comments?: string;
  label?: string;
}

export const CommentsComponents = forwardRef<HTMLDivElement, CommentsProps>(
  (props, ref) => {
    let unJsonComments = props.comments
      ? (JSON.parse(props.comments) as Comment[] | undefined)
      : undefined;
    if (unJsonComments) unJsonComments = [...unJsonComments];
    const [currentCommentId, setCurrentCommentId] = useState(1);
    const [currentComment, setCurrentComment] = useState<Comment | null>(null);

    useEffect(() => {
      if (!unJsonComments) return;

      const current =
        unJsonComments.find((comment) => comment.id === currentCommentId) ||
        null;
      const next = [];
      for (let i = 0; i < 3; i++) {
        const index = (currentCommentId + i) % unJsonComments.length;
        if (unJsonComments[index]) {
          next.push(unJsonComments[index]);
        }
      }

      setCurrentComment(current);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- disable exhaustive deps because we don't want to re-render the component
    }, [currentCommentId, unJsonComments?.length]);

    // TODO: check bold helvetica font weight
    return (
      <>
        {/* Comments Title Only Show In lg lower size */}
        <h1 className="font-helvetica lg:hidden block text-[25px] md:text-[40px] font-[100] leading-[1.07] text-left text-[#131d29] uppercase pb-7 md:pb-11">
          {props.label}
        </h1>
        <div
          className="w-full h-full grid grid-cols-1 md:grid-cols-5 lg:grid-cols-12 gap-3 lg:gap-[24px]"
          ref={ref}
        >
          <div className="md:col-start-3 col-start-1 col-span-1 md:col-span-2 order-last lg:order-first lg:col-span-4 flex flex-col justify-between">
            <h1 className="font-helvetica hidden lg:block text-[61.3px] font-[100] leading-[1.07] tracking-[-1.84px] text-left text-[#131d29] uppercase">
              {props.label}
            </h1>
            <div className="flex gap-2 w-full">
              <Carousel className="w-full">
                <CarouselContent className="flex items-center">
                  {unJsonComments?.map((comment) => (
                    <CarouselItem className="basis-1/4 pl-2" key={comment.id}>
                      <button
                        className="w-full h-[100px] md:h-[100px] relative group"
                        onClick={() => {
                          setCurrentCommentId(comment.id);
                        }}
                        type="button"
                      >
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors rounded-lg" />
                        <img
                          alt={`${comment.name} thumbnail ${comment.id}`}
                          className="w-full h-full object-cover object-center rounded-lg"
                          onError={(e) => {
                            e.currentTarget.src = "/images/placeholder.png";
                          }}
                          src={comment.image}
                        />
                        <div className="absolute bottom-1 left-1 right-1 text-white text-xs truncate">
                          {comment.name}
                        </div>
                      </button>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
          <div className="md:col-span-2 lg:col-span-3 col-span-1">
            <div className="relative h-full">
              <img
                alt={currentComment?.name}
                className="w-full h-full max-h-[300px] md:max-h-full md:min-h-[467.4px] object-cover rounded-lg"
                src={currentComment?.image}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg" />
            </div>
          </div>
          <div className="lg:col-span-5 md:col-span-3 col-span-1">
            <div className="w-full h-full bg-[#ececec] flex flex-col justify-between pt-[80px] p-[30px] relative rounded-lg">
              <div className="absolute top-1/2 -translate-y-1/3 right-[34px] w-[173px] h-[153px]">
                <QuoteIcon />
              </div>
              <p className="font-helvetica text-[22px] md:text-[29px] lg:text-[38.2px] font-[100] leading-[1.07] text-left text-[#1f2c51] text-wrap break-words">
                {currentComment?.comment}
              </p>
              <div className="flex gap-1 flex-col mt-12">
                <div className="flex gap-4">
                  {currentComment?.social?.map((social) => (
                    <a
                      className="hover:opacity-80 transition-opacity"
                      href={social.url}
                      key={social.url}
                      rel="noopener"
                      target="_blank"
                    >
                      <ImageIcon className="w-[24px] h-[24px]" />
                    </a>
                  ))}
                </div>
                <h5 className="font-helvetica text-[23.6px] font-medium">
                  {currentComment?.name}
                </h5>
                <p className="font-helvetica text-[15.7px] text-gray-600">
                  {currentComment?.company}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
);
CommentsComponents.displayName = "CommentsComponents";

export const Comments = observer(
  CommentsComponents
) as unknown as ComponentType<unknown>;

interface SettingTemplateProps {
  activeSettings: ComponentSettings;
  template: t.Template;
}

const SettingTemplate = observer((props: SettingTemplateProps) => {
  const editor = useEditor();
  const prop = "comments";
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState({
    name: "",
    comment: "",
    company: "",
    image: "",
  });
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  useEffect(() => {
    const fetchComments = (): void => {
      const storedComments =
        props.template.props[prop] instanceof t.Literal
          ? String(props.template.props[prop].value)
          : (props.activeSettings.props[prop].default as string) || "[]";
      try {
        const parsedComments = JSON.parse(storedComments) as Comment[];
        setComments(parsedComments);
      } catch (e) {
        setComments([]);
      }
    };
    fetchComments();
  }, []);

  const saveComments = (updatedComments: Comment[]): void => {
    void editor.reka.change(() => {
      props.template.props[prop] = t.literal({
        value: JSON.stringify(updatedComments),
      });
    });
  };

  const handleAddComment = (): void => {
    const newCommentData: Comment = {
      id: comments.length + 1,
      name: newComment.name,
      comment: newComment.comment,
      image:
        newComment.image ||
        `https://picsum.photos/200/300?random=${comments.length + 1}`,
      social: [
        {
          type: "instagram",
          url: `https://www.instagram.com/${comments.length + 1}`,
        },
      ],
      company: newComment.company,
    };

    const updatedComments = [...comments, newCommentData];
    saveComments(updatedComments);
    setComments(updatedComments);
    setNewComment({ name: "", comment: "", company: "", image: "" });
  };

  const handleUpdateComment = (
    id: number,
    updatedData: Partial<Comment>
  ): void => {
    const updatedComments = comments.map((comment) =>
      comment.id === id ? { ...comment, ...updatedData } : comment
    );
    setComments(updatedComments);
    saveComments(updatedComments);
    setUpdateDialogOpen(false);
    setEditingComment(null);
  };

  const deleteComment = (id: number): void => {
    const updatedComments = comments.filter((comment) => comment.id !== id);
    setComments(updatedComments);
    saveComments(updatedComments);
  };

  return (
    <div>
      <div className="flex px-5 py-3 mt-4 shadow rounded-lg mb-7">
        <div className="flex items-center flex-1">
          <div className="flex-1 text-sm">
            <span className="text-lg color-gray-800 w-full flex items-center mt-1 cursor-pointer">
              {props.activeSettings.label}
            </span>
          </div>
        </div>
      </div>
      <div>
        <LabelSetting
          activeSettings={props.activeSettings}
          prop="label"
          template={props.template}
        />
      </div>
      <div>
        <div className="flex flex-col gap-4">
          {comments.map((comment) => (
            <Card className="p-4 rounded-lg" key={comment.id}>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 shrink-0">
                  <img
                    alt={comment.name}
                    className="h-12 w-12 object-cover rounded-lg"
                    onError={(e) => {
                      e.currentTarget.src = "/images/placeholder.png";
                    }}
                    src={comment.image}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-medium truncate">
                        {comment.name}
                      </h3>
                      <p className="text-sm text-gray-500">{comment.company}</p>
                    </div>
                    <div className="text-right">
                      <span className="font-medium">نظر {comment.id}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <button
                      className="p-1 hover:bg-gray-200 rounded"
                      onClick={() => {
                        deleteComment(comment.id);
                      }}
                      type="button"
                    >
                      <TrashIcon size={18} />
                    </button>
                    <button
                      className="p-1 hover:bg-gray-200 rounded"
                      onClick={() => {
                        setEditingComment(comment);
                        setUpdateDialogOpen(true);
                      }}
                      type="button"
                    >
                      <EditIcon size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}

          <Dialog>
            <DialogTrigger asChild>
              <Button className="w-full" variant="outline">
                افزودن نظر جدید
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>افزودن نظر جدید</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right" htmlFor="name">
                    نام
                  </Label>
                  <div className="col-span-3">
                    <Input
                      id="name"
                      onChange={(e) => {
                        setNewComment({ ...newComment, name: e.target.value });
                      }}
                      value={newComment.name}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right">تصویر</Label>
                  <div className="col-span-3">
                    <ImagePicker
                      onImageSelect={(url) => {
                        setNewComment({ ...newComment, image: url });
                      }}
                      selectedImage={newComment.image}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right" htmlFor="company">
                    شرکت
                  </Label>
                  <div className="col-span-3">
                    <Input
                      id="company"
                      onChange={(e) => {
                        setNewComment({
                          ...newComment,
                          company: e.target.value,
                        });
                      }}
                      value={newComment.company}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label className="text-right" htmlFor="comment">
                    نظر
                  </Label>
                  <div className="col-span-3">
                    <Textarea
                      id="comment"
                      onChange={(e) => {
                        setNewComment({
                          ...newComment,
                          comment: e.target.value,
                        });
                      }}
                      rows={3}
                      value={newComment.comment}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleAddComment}>ذخیره نظر</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Update Dialog */}
      <Dialog onOpenChange={setUpdateDialogOpen} open={updateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>ویرایش نظر</DialogTitle>
          </DialogHeader>
          {editingComment ? (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right" htmlFor="edit-name">
                  نام
                </Label>
                <div className="col-span-3">
                  <Input
                    id="edit-name"
                    onChange={(e) => {
                      setEditingComment({
                        ...editingComment,
                        name: e.target.value,
                      });
                    }}
                    value={editingComment.name}
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right">تصویر</Label>
                <div className="col-span-3">
                  <ImagePicker
                    onImageSelect={(url) => {
                      setEditingComment({
                        ...editingComment,
                        image: url,
                      });
                    }}
                    selectedImage={editingComment.image}
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right" htmlFor="edit-company">
                  شرکت
                </Label>
                <div className="col-span-3">
                  <Input
                    id="edit-company"
                    onChange={(e) => {
                      setEditingComment({
                        ...editingComment,
                        company: e.target.value,
                      });
                    }}
                    value={editingComment.company || ""}
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right" htmlFor="edit-comment">
                  نظر
                </Label>
                <div className="col-span-3">
                  <Textarea
                    id="edit-comment"
                    onChange={(e) => {
                      setEditingComment({
                        ...editingComment,
                        comment: e.target.value,
                      });
                    }}
                    rows={3}
                    value={editingComment.comment}
                  />
                </div>
              </div>
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setUpdateDialogOpen(false);
                setEditingComment(null);
                saveComments(comments);
              }}
              variant="outline"
            >
              انصراف
            </Button>
            <Button
              onClick={() => {
                if (editingComment) {
                  handleUpdateComment(editingComment.id, editingComment);
                }
              }}
            >
              بروزرسانی
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export const CommentsSettings: ComponentSettings = {
  name: "EComments",
  props: commentsDefinition,
  settingTemplate: SettingTemplate,
  label: "بخش نظرات",
  widgets: {
    label: "بخش نظرات",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "EComments", external: true }),
      }),
  },
};

Comments.componentSettings = CommentsSettings;

function ImagePicker({
  onImageSelect,
  selectedImage,
}: {
  onImageSelect: (url: string) => void;
  selectedImage?: string;
}): React.JSX.Element {
  const [imagesDialogShow, setImagesDialogShow] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [images, setImages] = useState<MediaType[] | null>(null);
  const [pagination, setPagination] = useState<{ total_pages: number } | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);

  const handlePageChange = (page: number): void => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    const repository = new MediaRepository();
    setImagesLoading(true);
    void repository
      .getMany({
        sort: "-id",
        page: currentPage,
      })
      .then((res) => {
        if (res.succeeded && res.data) {
          setImages(res.data);
          setPagination(res.pagination);
        }
      })
      .finally(() => {
        setImagesLoading(false);
      });
  }, [currentPage]);

  return (
    <Dialog onOpenChange={setImagesDialogShow} open={imagesDialogShow}>
      <DialogTrigger asChild>
        {selectedImage ? (
          <div className="w-full group relative mt-4 rounded-lg">
            <Image
              alt=""
              className="w-full h-[200px] object-cover rounded-lg"
              height={200}
              onError={(e) => {
                e.currentTarget.src = "/images/placeholder.png";
              }}
              src={selectedImage}
              width={200}
            />
            <button
              className="hidden group-hover:flex outline-none flex-col absolute top-0 bottom-0 left-0 right-0 bg-[#00000050] items-center justify-center rounded-lg"
              onClick={() => {
                setImagesDialogShow(true);
              }}
              type="button"
            >
              <CameraIcon className="text-white w-8 h-8" />
              <span className="text-white">تغییر تصویر</span>
            </button>
            <Button
              className="hidden group-hover:flex absolute w-8 h-8 -top-1 -right-1 text-white"
              onClick={(e) => {
                onImageSelect("");
                e.stopPropagation();
              }}
              size="icon"
              variant="destructive"
            >
              <XIcon />
            </Button>
          </div>
        ) : (
          <Button className="w-full" type="button" variant="secondary">
            انتخاب تصویر
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] sm:max-h-[400px] md:max-w-[600px] md:max-h-[500px] lg:max-w-[1250px] lg:max-h-[700px] overflow-auto">
        <DialogHeader>
          <DialogTitle>تصاویر</DialogTitle>
          {imagesLoading ? (
            <div className="flex items-center justify-center py-56">
              <LoaderCircle className="animate-spin" size={30} />
            </div>
          ) : (
            <div className="pt-4 gap-7 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
              {images && images.length > 0 ? (
                images.map((image) => (
                  <button
                    className="text-start group outline-none"
                    key={image.id}
                    onClick={() => {
                      onImageSelect(image.url);
                      setImagesDialogShow(false);
                    }}
                    type="button"
                  >
                    <div className="relative">
                      <div className="rounded-lg hidden group-hover:flex absolute top-0 left-0 right-0 bottom-0 bg-[#00000070] items-center justify-center text-white">
                        <span className="text-bold text-xl">انتخاب</span>
                      </div>
                      <Image
                        alt={image.alt || ""}
                        className="w-full h-[200px] object-cover rounded-lg"
                        height={200}
                        onError={(e) => {
                          e.currentTarget.src = "/images/placeholder.png";
                        }}
                        src={image.url}
                        width={200}
                      />
                    </div>
                    {image.title ? (
                      <>
                        <h3 className="text-lg text-bold text-nowrap overflow-hidden text-ellipsis whitespace-nowrap">
                          {image.title}
                        </h3>
                        {image.description ? (
                          <p className="text-sm text-nowrap overflow-hidden text-ellipsis whitespace-nowrap">
                            {image.description}
                          </p>
                        ) : null}
                      </>
                    ) : null}
                  </button>
                ))
              ) : (
                <div className="flex items-center justify-center md:col-span-2 lg:col-span-3 py-40">
                  <p className="text-center">تصویری یافت نشد</p>
                </div>
              )}
            </div>
          )}
        </DialogHeader>
        {pagination ? (
          <Pagination>
            <PaginationContent>
              {currentPage !== 1 && (
                <PaginationItem>
                  <PaginationPrevious
                    isActive={currentPage === 1}
                    onClick={() => {
                      handlePageChange(currentPage - 1);
                    }}
                  />
                </PaginationItem>
              )}
              {Array.from({ length: pagination.total_pages }, (_, index) => (
                <PaginationItem key={index}>
                  <PaginationLink
                    isActive={currentPage === index + 1}
                    onClick={() => {
                      handlePageChange(index + 1);
                    }}
                  >
                    {index + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {currentPage !== pagination.total_pages && (
                <PaginationItem>
                  <PaginationNext
                    isActive={currentPage === pagination.total_pages}
                    onClick={() => {
                      handlePageChange(currentPage + 1);
                    }}
                  />
                </PaginationItem>
              )}
            </PaginationContent>
          </Pagination>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface LabelSettingProps {
  prop: string;
  template: t.Template;
  activeSettings: ComponentSettings;
}

const LabelSetting = observer(({ prop, template }: LabelSettingProps) => {
  const editor = useEditor();

  return (
    <div className="mb-2 w-full border border-gray-200 rounded-md p-2">
      <span className="inline-block text-sm mr-2 pb-1 flex-1">عنوان</span>
      <Input
        className="flex-2"
        onChange={(e) => {
          void editor.reka.change(() => {
            template.props[prop] = t.literal({
              value: String(e.target.value),
            });
          });
        }}
        type="text"
        value={
          template.props[prop] instanceof t.Literal
            ? String(template.props[prop].value)
            : ""
        }
      />
    </div>
  );
});
